import { ButtonFinal, HeaderPopup, Icons, Popup, Switch } from "@kalyzee/escaet-ui";
import React, { createRef, ForwardedRef, useEffect, useState } from "react";
import { ReactComponent as IconInfo } from "../../assets/icons/info.svg";
import { appMessage } from "../../helpers/message";
import { getExpertHomeParams, setExpertHomeParams } from "../../helpers/storage";
import { APP_VERSION } from "../../helpers/utils";
import { useMicrophone, useWrapperAppData } from "../../store/session/hooks";
import OverlayInformationMessage, { OverlayInformationMessageProps } from "./OverlayInformationMessage";

export interface ExpertSettingsPopupProps {
  onClose: () => void;
}

export interface ExpertSettingsPopupRef {}

const ExpertSettingsPopup = React.forwardRef(({ onClose }: ExpertSettingsPopupProps, forwardRef: ForwardedRef<ExpertSettingsPopupRef | undefined>) => {
  const microphone = useMicrophone();
  const wrapperAppData = useWrapperAppData();
  const [homeParams, setHomeParams] = useState(() => getExpertHomeParams());
  useEffect(() => {
    appMessage.getMicrohoneData();
    appMessage.getAppData();
  }, []);
  const [versions, setVersions] = useState<{ version: string; date: string; data: string[] }[]>();

  const loadVersions = async () => {
    try {
      const response = await fetch("/versions.json");
      const json = await response.json();
      if (Array.isArray(json)) {
        setVersions(json);
      }
    } catch (err) {}
  };

  useEffect(() => {
    loadVersions();
  }, []);

  const renderSwitchRow = (
    title: string,
    checked: boolean,
    onCheck: (value: boolean) => void,
    moreInfo?: string | Omit<OverlayInformationMessageProps, "targetRef">
  ) => {
    const moreInfoContainerRef = createRef<HTMLDivElement>();
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "5px" }}>
          {title}
          {moreInfo ? (
            <div ref={moreInfoContainerRef} style={{ padding: "5px" }}>
              <IconInfo width={20} height={20} />
              {typeof moreInfo === "string" ? (
                <OverlayInformationMessage delay={500} targetRef={moreInfoContainerRef} message={moreInfo} />
              ) : (
                <OverlayInformationMessage delay={500} targetRef={moreInfoContainerRef} {...moreInfo} />
              )}
            </div>
          ) : null}
          {":"}
        </div>

        <Switch
          name="mute"
          sliderColor={"white"}
          color={checked ? "#05C496" : "#F05E5E"}
          checked={checked}
          onChange={(event: any) => {
            const value = event.target.checked;
            onCheck(value);
          }}
        />
      </div>
    );
  };

  const renderVersion = () => {
    const current = APP_VERSION;
    const styles = {
      colonnes: {
        version: { width: "150px" },
        date: { width: "150px" },
        data: { width: "auto" },
      },
    };
    return (
      <div
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          marginTop: "20px",
        }}
      >
        <div style={{ width: "100%" }}>{`Version: ${current}`}</div>
        {versions ? (
          <>
            <a href="/versions.json" target="_blank" style={{ width: "100%", textDecoration: "underline", color: "blue" }}>{`Historique des versions`}</a>
            <div
              style={{
                width: "100%",
                flex: "1 1 auto",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                borderRadius: "5px",
                backgroundColor: "#33333311",
              }}
            >
              <table style={{ width: "calc(100% - 20px)", borderCollapse: "separate", borderSpacing: "0 10px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", ...styles.colonnes.version }}>Version</th>
                    <th style={styles.colonnes.date}>Date</th>
                    <th style={styles.colonnes.data}>Changements</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((release, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: "left", fontWeight: "bold", verticalAlign: "top", ...styles.colonnes.version }}>{release.version}</td>
                      <td style={{ textAlign: "center", verticalAlign: "top", ...styles.colonnes.date }}>{release.date}</td>
                      <td style={{ verticalAlign: "top", ...styles.colonnes.data }}>
                        <ul>
                          {release.data.map((item, i) => (
                            <li style={{ listStyleType: "disc" }} key={i}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <Popup
      style={{
        width: "min(80%)",
        height: "min(80%)",
      }}
      Header={<HeaderPopup icon={Icons.Settings} title={"Paramètres"} className="flex !flex-row" />}
      Content={
        <div style={{ width: "80%", maxHeight: "100%", display: "flex", flexDirection: "column", paddingBottom: "20px" }}>
          {microphone
            ? renderSwitchRow(
                `Activation du microphone en dehors de SAMII`,
                !homeParams?.micOffOutsideOfAnEvent,
                (v) => {
                  appMessage.setMicrophoneData(!v, !v);
                  const newHomeParams = { ...(homeParams ?? {}) };
                  newHomeParams.micOffOutsideOfAnEvent = !v;
                  setHomeParams(newHomeParams);
                  setExpertHomeParams(newHomeParams);
                },
                {
                  message:
                    "ON: La dalle reste active en dehors des cours SAMII\nOFF: Seul le démarrage d’un cours SAMII active la dalle. La dalle se désactive dès que le cours se termine",
                  style: { height: 80 },
                }
              )
            : null}
          {renderVersion()}
          {wrapperAppData?.canClose ? (
            <ButtonFinal
              text="Fermer l'application"
              image={Icons.Cancel}
              iconStyle={{ color: "white" }}
              background="linear-gradient(to bottom, #EA6B6B, #F05E5E)"
              textColor="#fff"
              cx="!rounded-[40px] !font-[800] !h-[68px] !text-[20px]"
              style={{
                width: "100%",
                marginTop: 20,
                alignItems: "center",
                justifyContent: "center"
              }}
              onClick={() => {
                appMessage.postCloseApp()
              }}
            />
          ) : null}
        </div>
      }
      // cx="dashboardPopupStyle" TODO_REFACTOR : add this back
      iconClose={Icons.CloseIconBlackBg}
      onClose={onClose}
    />
  );
});

export default ExpertSettingsPopup;
